/**
 * Orchestrator — the pipeline conductor. Coordination only.
 *
 *   prompt → provider (LLM; mock today) → Creative Director
 *          → Template Factory → Design Engine → results
 *
 * No design logic, no rendering, no editor logic lives here. Phases 1–3
 * are consumed strictly through their public facades. Every stage emits a
 * progress event, checks cancellation, and provider calls are retried.
 */

import { direct, toFactoryPlan } from '../aiCreativeDirector';
import { manufacture, GenerationHistory } from '../aiTemplateFactory';
import { generateTemplate } from '../aiDesignEngine';
import { buildCreativeBriefPrompt } from './promptBuilder';
import { parseCreativeBriefCall } from './functionSchema';
import { mockProvider } from './mockProvider';
import { hashString } from './mockProvider';
import { ConversationContext } from './conversationContext';
import { withRetry } from './retryManager';
import { CancellationToken, CancelledError } from './cancellation';
import { STAGES, createProgressEmitter } from './progressEvents';

/**
 * Run the full pipeline for one prompt.
 *
 * options = {
 *   prompt              — the user's design request (string)
 *   provider            — AIProvider (default: offline mock)
 *   context             — ConversationContext (default: fresh)
 *   count = 1           — templates to manufacture
 *   pins = {}           — admin pins { audience?, mood?, brandStyle? };
 *                         pins ALWAYS beat provider decisions
 *   seed                — pipeline seed (default: prompt hash, deterministic)
 *   minScore            — quality gate override
 *   history             — GenerationHistory (default: fresh, so the
 *                         playground never fights the factory console)
 *   onProgress(event)   — stage events (see progressEvents.js)
 *   token               — CancellationToken
 *   retry               — retryManager options for the provider call
 * }
 *
 * Returns { brief, dropped, direction, plan, templates, stats, providerMessage }
 * where each template additionally carries `commands` (Design Engine output)
 * for preview. Throws CancelledError on cancellation.
 */
export const generateFromPrompt = async ({
  prompt = '',
  provider = mockProvider,
  context = new ConversationContext(),
  count = 1,
  pins = {},
  seed,
  minScore,
  history = new GenerationHistory(),
  onProgress,
  token = new CancellationToken(),
  retry = {},
} = {}) => {
  const emit = createProgressEmitter(onProgress);
  const pipelineSeed = seed ?? hashString(prompt);

  try {
    emit(STAGES.QUEUED, { prompt, provider: provider.id, count });
    token.throwIfCancelled();

    // ── 1. Provider decides the brief (LLM later, mock today) ──────────────
    emit(STAGES.THINKING, { provider: provider.name });
    const request = buildCreativeBriefPrompt(prompt, pins);
    context.add('system', request.system);
    context.addUser(request.user);

    const providerMessage = await withRetry(
      () => provider.generate(request, context),
      { ...retry, token }
    );
    token.throwIfCancelled();

    const { brief, dropped } = parseCreativeBriefCall(
      providerMessage?.type === 'function_call' ? providerMessage.arguments : {}
    );
    // Admin pins always win over the provider.
    if (pins.audience) brief.audience = pins.audience;
    if (pins.mood) brief.mood = pins.mood;
    if (pins.brandStyle) brief.brandStyle = pins.brandStyle;
    brief.seed = pipelineSeed;
    context.addFunctionResult('decide_creative_brief', brief);

    // ── 2. Creative Director ────────────────────────────────────────────────
    emit(STAGES.CREATIVE_DIRECTION, { brief });
    const direction = direct({ ...brief, force: Boolean(pins.mood || pins.brandStyle) });
    token.throwIfCancelled();

    // ── 3. Template Factory (which itself runs the Design Engine gate) ─────
    const plan = toFactoryPlan(direction, minScore ? { minScore } : {});
    emit(STAGES.TEMPLATE_FACTORY, { category: plan.category, overrides: plan.overrides });
    const { templates, stats, complete } = manufacture({
      ...plan,
      count,
      seed: pipelineSeed,
      history,
    });
    token.throwIfCancelled();

    // ── 4. Design Engine pass for command previews ──────────────────────────
    emit(STAGES.DESIGN_ENGINE, { accepted: templates.length });
    const withCommands = templates.map((t) => ({
      ...t,
      commands: generateTemplate(t.designSpec).commands,
    }));
    token.throwIfCancelled();

    const result = {
      brief, dropped, direction, plan,
      templates: withCommands, stats, complete,
      providerMessage,
    };
    emit(STAGES.COMPLETED, { accepted: withCommands.length, attempts: stats.attempts });
    return result;
  } catch (error) {
    if (error instanceof CancelledError) {
      emit(STAGES.CANCELLED, { reason: token.reason });
    } else {
      emit(STAGES.ERROR, { message: error.message });
    }
    throw error;
  }
};
